// src/components/global/Notification/NotificationBell.js
// VERSION: 2.1 - FIXED: Better chat notification handling

import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MdNotifications, MdOutlineMarkunreadMailbox } from "react-icons/md";
import { FaTicketAlt, FaMicroscope, FaIndustry } from "react-icons/fa";
import { IoChatboxEllipsesOutline } from "react-icons/io5";
import { BiPurchaseTag } from "react-icons/bi";
import { FcApproval, FcSalesPerformance } from "react-icons/fc";
import { GoBeaker } from "react-icons/go";
import { CiSettings } from "react-icons/ci";
import {
    NOTIFICATION_MODULES,
    PRIORITY_COLORS,
    getModuleConfig
} from '../../../config/notificationConfig';
import styles from './NotificationBell.module.css';

// Define the maps locally since they're not exported
const MODULE_ICON_MAP = {
    'helpdesk': <FaTicketAlt />,
    'chat': <IoChatboxEllipsesOutline />,
    'requisicao_de_compra': <BiPurchaseTag />,
    'aprovacao_de_pedido_de_compra': <FcApproval />,
    'pedido_de_venda': <FcSalesPerformance />,
    'desenvolvimento': <GoBeaker />,
    'acompanhamento_de_projeto': <FaMicroscope />,
    'ordens_de_producao': <FaIndustry />,
    'fases_de_producao': <CiSettings />,
    'default': <MdNotifications />
};

const MODULE_COLOR_MAP = {
    'helpdesk': '#0d6efd',
    'finance': '#198754',
    'inventory': '#ffc107',
    'quality': '#20c997',
    'chat': '#6f42c1',
    'crm': '#fd7e14',
    'hr': '#dc3545',
    'projects': '#0dcaf0',
    'default': '#6c757d'
};

const MODULE_NAME_MAP = {
    'helpdesk': 'Helpdesk',
    'finance': 'Financeiro',
    'inventory': 'Inventário',
    'quality': 'Qualidade',
    'chat': 'Chat',
    'crm': 'CRM',
    'hr': 'RH',
    'projects': 'Projetos',
    'default': 'Módulo'
};

const PRIORITY_EMOJIS = {
    'high': '🔴',
    'medium': '🟡',
    'low': '🟢',
    'critical': '🚨',
    'default': ''
};

const NotificationBell = ({ onChatNotificationClick }) => {
    const { t } = useTranslation();
    const {
        notifications,
        unreadCount,
        markAsRead,
        clearNotification,
        clearAllNotifications,
        markAllAsRead,
        isLoading
    } = useNotifications();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleNotificationClick = (notification) => {
        if (!notification) return;

        // Mark as read
        markAsRead(notification.id);

        // Handle chat notifications differently
        if (notification.module === 'chat' || notification.isChatNotification) {
            // Call the parent handler if provided
            if (onChatNotificationClick) {
                onChatNotificationClick(notification);
                setIsOpen(false);
                return;
            }

            // Fallback: dispatch event to open chat
            const event = new CustomEvent('openChat', {
                detail: {
                    channelId: notification.channelId || notification.data?.channelId,
                    notification: notification
                }
            });
            window.dispatchEvent(event);
            setIsOpen(false);
            return;
        }

        // Handle regular navigation
        if (notification.route) {
            navigate(notification.route);
            setIsOpen(false);
        }
    };

    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return t('notifications.now');

        let date;
        if (timestamp && timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else if (timestamp && typeof timestamp === 'object' && timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else if (timestamp) {
            date = new Date(timestamp);
        } else {
            return t('notifications.now');
        }

        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return t('notifications.now');
        if (diffMins < 60) return `${diffMins} ${t('notifications.minAgo')}`;
        if (diffHours < 24) return `${diffHours} ${t('notifications.hAgo')}`;
        return `${diffDays} ${t('notifications.dAgo')}`;
    };

    const getModuleColor = (module) => {
        return MODULE_COLOR_MAP[module] || MODULE_COLOR_MAP.default;
    };

    const getModuleIcon = (module) => {
        return MODULE_ICON_MAP[module] || MODULE_ICON_MAP.default;
    };

    const getModuleName = (module) => {
        return MODULE_NAME_MAP[module] || module || 'Módulo';
    };

    const getPriorityEmoji = (priority) => {
        return PRIORITY_EMOJIS[priority] || PRIORITY_EMOJIS.default;
    };

    const getPriorityColor = (priority) => {
        return PRIORITY_COLORS[priority] || '#6c757d';
    };

    // Agrupar notificações por módulo para exibição
    const groupedNotifications = notifications.reduce((acc, n) => {
        const module = n.module || 'helpdesk';
        if (!acc[module]) acc[module] = [];
        acc[module].push(n);
        return acc;
    }, {});

    // Ordenar módulos pelo número de não lidas
    const sortedModules = Object.keys(groupedNotifications).sort((a, b) => {
        const unreadA = groupedNotifications[a].filter(n => !n.read).length;
        const unreadB = groupedNotifications[b].filter(n => !n.read).length;
        return unreadB - unreadA;
    });

    // Don't render while loading
    if (isLoading) {
        return null;
    }

    // Only hide bell if there are truly no notifications
    if ((!notifications || notifications.length === 0) && unreadCount === 0) {
        return null;
    }

    return (
        <div className={styles.bellContainer} ref={dropdownRef}>
            <button
                className={styles.bellButton}
                onClick={() => setIsOpen(!isOpen)}
                title={t('notifications.bellTitle')}
                aria-label="Notificações"
            >
                <span className={styles.bellIcon}><MdNotifications /></span>
                {unreadCount > 0 && (
                    <span className={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>
                )}
            </button>

            {isOpen && (
                <div className={styles.dropdown}>
                    <div className={styles.dropdownHeader}>
                        <h3>
                            <span className={styles.headerIcon}>🔔</span>
                            {t('notifications.title')}
                            {unreadCount > 0 && (
                                <span className={styles.headerCount}>{unreadCount}</span>
                            )}
                        </h3>
                        {notifications && notifications.length > 0 && (
                            <button
                                className={styles.markAllRead}
                                onClick={markAllAsRead}
                                title={t('notifications.markAllRead')}
                            >
                                {t('notifications.markAllRead')}
                            </button>
                        )}
                    </div>

                    <div className={styles.notificationsList}>
                        {!notifications || notifications.length === 0 ? (
                            <div className={styles.emptyState}>
                                <span className={styles.emptyIcon}><MdOutlineMarkunreadMailbox /></span>
                                <p className={styles.emptyText}>{t('notifications.empty')}</p>
                            </div>
                        ) : (
                            sortedModules.map((module) => {
                                const moduleNotifications = groupedNotifications[module];
                                const moduleName = getModuleName(module);
                                const moduleIcon = getModuleIcon(module);
                                const moduleColor = getModuleColor(module);
                                const unreadInModule = moduleNotifications.filter(n => !n.read).length;

                                return (
                                    <React.Fragment key={module}>
                                        <div
                                            className={styles.moduleHeader}
                                            style={{ borderLeftColor: moduleColor }}
                                        >
                                            <span className={styles.moduleIcon}>{moduleIcon}</span>
                                            <span className={styles.moduleName}>{moduleName}</span>
                                            {unreadInModule > 0 && (
                                                <span className={styles.moduleCount} style={{ background: moduleColor }}>
                                                    {unreadInModule}
                                                </span>
                                            )}
                                        </div>
                                        {moduleNotifications.slice(0, 20).map(notification => {
                                            const priorityEmoji = getPriorityEmoji(notification.priority);
                                            const priorityColor = getPriorityColor(notification.priority);
                                            const isUnread = !notification.read;

                                            // Verificar se é notificação de chat para exibição especial
                                            const isChat = notification.module === 'chat' || notification.isChatNotification;
                                            const chatSender = notification.senderName || '';
                                            const chatMessage = notification.message || '';

                                            return (
                                                <div
                                                    key={notification.id}
                                                    className={`${styles.notificationItem} ${isUnread ? styles.unread : ''}`}
                                                    onClick={() => handleNotificationClick(notification)}
                                                >
                                                    <div className={styles.notificationIcon} style={{ color: moduleColor }}>
                                                        {isUnread && <span className={styles.unreadDot}></span>}
                                                        <span className={styles.iconEmoji}>{moduleIcon}</span>
                                                    </div>
                                                    <div className={styles.notificationContent}>
                                                        <div className={styles.notificationTitle}>
                                                            {isChat ? (
                                                                <span>💬 {chatSender}</span>
                                                            ) : (
                                                                notification.title || t('notifications.defaultTitle')
                                                            )}
                                                            {notification.priority && (
                                                                <span
                                                                    className={styles.priorityBadge}
                                                                    style={{
                                                                        background: priorityColor + '20',
                                                                        color: priorityColor,
                                                                        borderColor: priorityColor
                                                                    }}
                                                                >
                                                                    {priorityEmoji} {notification.priority}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className={styles.notificationMessage}>
                                                            {isChat ? chatMessage : (notification.message || '')}
                                                        </div>
                                                        <div className={styles.notificationMeta}>
                                                            <span className={styles.notificationTime}>
                                                                {formatTimeAgo(notification.timestamp)}
                                                            </span>
                                                            {notification.moduleName && (
                                                                <span
                                                                    className={styles.moduleTag}
                                                                    style={{
                                                                        background: moduleColor + '20',
                                                                        color: moduleColor
                                                                    }}
                                                                >
                                                                    {moduleIcon} {moduleName}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <button
                                                        className={styles.closeButton}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (notification && notification.id) {
                                                                clearNotification(notification.id);
                                                            }
                                                        }}
                                                        aria-label="Fechar notificação"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })
                        )}
                    </div>

                    {notifications && notifications.length > 0 && (
                        <div className={styles.dropdownFooter}>
                            <button
                                className={styles.clearAllButton}
                                onClick={clearAllNotifications}
                            >
                                {t('notifications.clearAll')}
                            </button>
                            <span className={styles.footerInfo}>
                                {notifications.length} {t('notifications.total')}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;